import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Download } from 'lucide-react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInvoices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/invoices`, { withCredentials: true });
      setInvoices(data);
    } catch (error) {
      toast.error('Eroare la încărcarea facturilor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleDownload = (invoice) => {
    window.open(`${API}/files/${invoice.file_path}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit' }}>
          Facturile Mele
        </h1>

        {invoices.length === 0 ? (
          <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
            Nu ai facturi momentan.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {invoices.map((invoice) => (
              <Card
                key={invoice.id}
                className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`invoice-${invoice.id}`}
              >
                <CardHeader className="bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="w-6 h-6 text-blue-900" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>
                        Factură #{(invoice.order_id || invoice.id).substring(0, 8).toUpperCase()}
                      </CardTitle>
                      <p className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
                        {invoice.original_filename}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2" style={{ fontFamily: 'IBM Plex Sans' }}>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total:</span>
                      <span className="font-bold text-blue-900" data-testid={`invoice-amount-${invoice.id}`}>
                        {invoice.amount.toFixed(2)} RON
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Achitat:</span>
                      <span className="font-semibold text-green-600" data-testid={`invoice-paid-${invoice.id}`}>
                        {(invoice.paid_amount || 0).toFixed(2)} RON
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Rest de plată:</span>
                      <span className="font-bold text-red-600" data-testid={`invoice-remaining-${invoice.id}`}>
                        {(invoice.amount - (invoice.paid_amount || 0)).toFixed(2)} RON
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Scadență:</span>
                      <span className="font-semibold" data-testid={`invoice-due-date-${invoice.id}`}>
                        {format(new Date(invoice.due_date), 'dd MMM yyyy', { locale: ro })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Status:</span>
                      <Badge
                        className={invoice.is_paid ? 'bg-green-100 text-green-800' : (invoice.paid_amount || 0) > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}
                        data-testid={`invoice-status-${invoice.id}`}
                      >
                        {invoice.is_paid ? 'Achitată' : (invoice.paid_amount || 0) > 0 ? 'Parțial' : 'Neplătită'}
                      </Badge>
                    </div>
                  </div>

                  {invoice.payments?.length > 0 && (
                    <div className="border-t border-slate-200 pt-3">
                      <p className="text-xs font-semibold text-slate-700 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>Plăți înregistrate:</p>
                      <div className="space-y-1">
                        {invoice.payments.map((p, idx) => (
                          <div key={idx} className="flex justify-between text-xs bg-green-50 border border-green-100 rounded px-2 py-1" style={{ fontFamily: 'IBM Plex Sans' }}>
                            <span className="text-slate-600">{format(new Date(p.date), 'dd MMM yyyy', { locale: ro })}{p.note ? ` · ${p.note}` : ''}</span>
                            <span className="font-semibold text-green-700">+{p.amount.toFixed(2)} RON</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => handleDownload(invoice)}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                    data-testid={`download-invoice-${invoice.id}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descarcă
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;